<script setup>
import Staff from '@pages/Shared/layouts/StaffLayout.vue'
import { ref } from 'vue'
import { PhUsers, PhChalkboardTeacher, PhBookOpen, PhClipboardText } from '@phosphor-icons/vue'

const props = defineProps({
    staff: Object,
    // filters: Object
});

// Example stats data
const stats = ref([
  {
    title: 'Total Students',
    value: 125,
    icon: PhUsers,
    color: 'bg-primary-100 text-primary-700',
  },
  {
    title: 'Total Classes',
    value: 8,
    icon: PhChalkboardTeacher,
    color: 'bg-green-100 text-green-700',
  },
  {
    title: 'Subjects Handled',
    value: 5,
    icon: PhBookOpen,
    color: 'bg-yellow-100 text-yellow-700',
  },
  {
    title: 'Attendance Rate',
    value: '94%',
    icon: PhClipboardText,
    color: 'bg-purple-100 text-purple-700',
  },
])

// Example upcoming classes
const upcomingClasses = ref([
  { course: 'Mathematics', time: '08:00 AM - 09:30 AM', class: 'SS2A' },
  { course: 'Physics', time: '10:00 AM - 11:30 AM', class: 'SS3B' },
  { course: 'Chemistry', time: '12:00 PM - 01:30 PM', class: 'SS2B' },
])

// Example recent activity
const activities = ref([
  { text: 'Uploaded CA scores for SS2A - Mathematics', time: '2 hours ago' },
  { text: 'Marked attendance for SS3B - Physics', time: 'Yesterday' },
  { text: 'Updated profile information', time: '2 days ago' },
])
</script>

<template>
  <Staff>
    <div class="w-[90vw] md:max-w-[85vw] mx-auto py-6 space-y-8">
      <!-- ===== HEADER ===== -->
      <div class="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h2 class="text-2xl font-bold text-primary-700">Welcome Back, {{ props.staff.name }}</h2>
          <p class="text-gray-500 text-sm mt-1">
            Here’s a quick overview of your teaching performance and class activities.
          </p>
        </div>
        <button
          class="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-5 py-2 rounded-lg shadow transition"
        >
          View Full Report
        </button>
      </div>

      <!-- ===== STATISTIC CARDS ===== -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div
          v-for="(stat, index) in stats"
          :key="index"
          class="bg-white border border-gray-200 rounded-xl shadow-sm p-5 flex items-center gap-4 hover:shadow-md transition"
        >
          <div :class="['w-12 h-12 rounded-full flex items-center justify-center', stat.color]">
            <component :is="stat.icon" :size="24" />
          </div>
          <div>
            <h3 class="text-gray-600 text-sm font-medium">{{ stat.title }}</h3>
            <p class="text-2xl font-bold text-gray-800 mt-1">{{ stat.value }}</p>
          </div>
        </div>
      </div>

      <!-- ===== TWO COLUMN SECTION ===== -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Upcoming Classes -->
        <section class="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 class="text-gray-700 font-semibold text-lg border-b border-gray-200 pb-3 mb-3">
            Upcoming Classes
          </h3>
          <div v-for="(item, index) in upcomingClasses" :key="index" class="mb-3">
            <div class="flex justify-between items-center">
              <h4 class="font-semibold text-primary-700 text-sm">{{ item.course }}</h4>
              <span class="text-xs text-gray-500">{{ item.time }}</span>
            </div>
            <p class="text-xs text-gray-600">Class: {{ item.class }}</p>
            <hr class="border-gray-100 mt-2" v-if="index < upcomingClasses.length - 1" />
          </div>
          <button
            class="mt-4 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-4 py-2 rounded-md transition"
          >
            View Timetable
          </button>
        </section>

        <!-- Recent Activities -->
        <section class="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 class="text-gray-700 font-semibold text-lg border-b border-gray-200 pb-3 mb-3">
            Recent Activities
          </h3>
          <ul>
            <li
              v-for="(activity, index) in activities"
              :key="index"
              class="flex items-start gap-3 mb-3 last:mb-0"
            >
              <span
                class="w-2.5 h-2.5 mt-2 bg-primary-500 rounded-full flex-shrink-0"
              ></span>
              <div>
                <p class="text-sm text-gray-700">{{ activity.text }}</p>
                <p class="text-xs text-gray-500">{{ activity.time }}</p>
              </div>
            </li>
          </ul>
        </section>
      </div>
    </div>
  </Staff>
</template>
